// Session BBO: a read-only "Recent email" panel for one engineer on the Engineers page. It lists the last few emails to
// and from the engineer's ONE recorded address, read live from the existing mail reads (the email engine's record of mail
// received, Gmail's Sent folder for mail sent). Nothing is stored or sent from here. A read that did not complete is said
// in words and is never shown as "no email". The matching is exact on the address; it is display only.
import { useState } from "react";
import { api } from "../../lib/api";
import type { EngineerRecentEmail } from "../../lib/api";
import { ModalShell, MODAL_ACTION_BTN, MODAL_ACTIONS } from "../../components/ModalShell";
import { Loader } from "../../components/Loader";
import { formatDateTime } from "../../lib/format";

export function EngineerRecentEmailButton({ userId, name }: { userId: string; name: string }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EngineerRecentEmail | null>(null);
  const [failed, setFailed] = useState(false);

  async function openModal() {
    setOpen(true);
    setLoading(true);
    setFailed(false);
    setData(null);
    try {
      setData(await api.mail.engineerEmail(userId));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="rounded-ele border border-ele-blue px-2.5 py-1 text-xs text-ele-blue transition hover:bg-ele-blue/10"
      >
        Recent email
      </button>
      {open && (
        <ModalShell ariaLabel="Recent email" onClose={() => setOpen(false)} width="max-w-lg" panelClassName="max-h-[90vh] overflow-y-auto p-6 text-left">
          <h2 className="mb-1 font-heading text-xl text-ele-strong">Recent email</h2>
          <p className="mb-4 text-sm text-ele-body">
            {name}
            {data?.address ? ` (${data.address})` : ""}
          </p>
          {loading ? (
            <Loader label="Reading the mailbox..." shape="inline" />
          ) : failed ? (
            <p role="alert" className="text-sm text-ele-danger">
              The mailbox could not be read, so no email is listed. This does not mean there is none. Try again shortly.
            </p>
          ) : data ? (
            <>
              {data.message && (
                <p role="alert" className="mb-3 text-sm text-ele-body">
                  {data.message}
                </p>
              )}
              {data.state === "no_address" ? null : data.emails.length === 0 ? (
                data.state === "failed" ? null : <p className="text-sm text-ele-body opacity-70">No email to or from this address in the mailbox.</p>
              ) : (
                <ul className="divide-y divide-ele-purple-accent/40 border-y border-ele-purple-accent/40">
                  {data.emails.map((e) => (
                    <li key={`${e.direction}-${e.gmailThreadId}-${e.at}`} className="py-2 text-sm text-ele-body">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-menu text-xs uppercase tracking-wider text-ele-blue">{e.direction === "inbound" ? "Received" : "Sent"}</span>
                        <span className="text-xs opacity-70">{formatDateTime(e.at)}</span>
                      </div>
                      <div className="text-ele-strong">{e.withheld ? "Withheld (payment details)" : (e.subject ?? "(no subject)")}</div>
                      {e.counterpart && (
                        <div className="text-xs opacity-70">
                          {e.direction === "inbound" ? "From" : "To"} {e.counterpart}
                        </div>
                      )}
                      <a href={e.gmailUrl} target="_blank" rel="noreferrer" className="text-xs text-ele-blue underline">
                        Open in Gmail
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}
          <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
            <button type="button" onClick={() => setOpen(false)} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}>
              Close
            </button>
          </div>
        </ModalShell>
      )}
    </>
  );
}
